import {
  type PlayerPublic,
  postRostersUnofficialUploadMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { useRef } from "react";

import { convertPlayersToCsv } from "@/utils/convertPlayersToCsv";

type HandleCreateRosterProps = {
  rosterName: string;
  parentName: string;
  selectedPlayers: PlayerPublic[];
};

/**
 * Creates a roster with existing players and sets the parent team name in a single API call.
 * The /rosters/unofficial/upload endpoint accepts both teamName (roster/age group name)
 * and parentName (actual team name) as query parameters.
 *
 * @param organizationId - The organization ID to create the roster for.
 * @param onSuccess - The function to call when the roster is created, receives the created roster data.
 * @returns The createRoster function, isLoadingRosterCreation, and resetRosterCreation.
 */
export const useCreateRosterWithExistingPlayer = ({
  organizationId,
  onSuccess,
}: {
  organizationId: string;
  onSuccess?: (data: { id: string; name: string; teamName: string }) => void;
}) => {
  const queryClient = useQueryClient();
  const rosterNameRef = useRef<string>("");
  const parentNameRef = useRef<string>("");

  const {
    mutate: uploadRoster,
    isPending: isLoadingRosterCreation,
    reset: resetRosterCreation,
  } = useMutation({
    ...postRostersUnofficialUploadMutation(),
    onError: (error) => {
      const createRosterErrorMessage = error
        ? JSON.parse((error as Error).message).detail
        : null;

      if (createRosterErrorMessage) {
        toast(createRosterErrorMessage, { variant: "error" });
      }
    },
    onSuccess: (data) => {
      toast("Roster created successfully!", { variant: "success" });

      queryClient.invalidateQueries({
        queryKey: ["getTeamsSearch"],
        exact: false,
      });

      const createdRoster = data?.team;
      const rosterName = rosterNameRef.current;
      const parentName = parentNameRef.current;

      onSuccess?.({
        id: createdRoster?.external_ids?.[0] || rosterName,
        name: rosterName,
        teamName: parentName,
      });
    },
  });

  const handleCreateRoster = async ({
    rosterName,
    parentName,
    selectedPlayers,
  }: HandleCreateRosterProps) => {
    rosterNameRef.current = rosterName;
    parentNameRef.current = parentName;
    const csvContent = convertPlayersToCsv(selectedPlayers);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const formData = new FormData();
    formData.append("file", blob, "roster.csv");

    uploadRoster({
      body: {
        file: [formData.get("file") as Blob],
      },
      path: {
        org_id: organizationId,
      },
      query: {
        orgId: organizationId,
        teamName: rosterName,
        parentName: parentName,
      },
    });
  };

  return {
    createRoster: handleCreateRoster,
    isLoadingRosterCreation,
    resetRosterCreation,
  };
};
